import * as classnames from 'classnames';
import * as React from 'react';
import { Link as ReactRouterDOMLink } from 'react-router-dom';
import Hoverable from '../Hoverable';
import If from '../If';
import Pressable from '../Pressable';
import * as styles from './Link.sass';

const cx = classnames.bind(styles);

const noop = () => null;

interface LinkProps {
  children: React.ReactNode;
  isInline: boolean;
  to: string;
  onClick: () => void;
  color: 'purple' | 'gray';
  target: 'null' | '_self' | '_blank';
  isIncludingSpacesAround: boolean;
}
interface LinkableProps {
  className: string;
  style: any;
  children: (args: {
    isHovering: boolean;
    isPressed: boolean;
  }) => React.ReactNode;
  isInline: boolean;
  to: string;
  onClick: () => void;
  color: 'purple' | 'gray';
  target: 'null' | '_self' | '_blank';
  isFullWidth: boolean;
}

const linkTextDefaultProps = {
  isInline: false,
  onClick: noop,
  target: 'null',
  color: 'purple',
  isIncludingSpacesAround: false,
};
const linkableDefaultProps = {
  isInline: false,
  onClick: noop,
  target: 'null',
  color: 'purple',
  isIncludingSpacesAround: false,
  className: '',
  style: {},
  isFullWidth: false,
};
const addSpacesAround = (element: React.ReactNode) => (
  <React.Fragment>
    {'\u00A0'}
    {element}
    {'\u00A0'}
  </React.Fragment>
);

const isUrlExternal = (url: string) => /^https?:\/\//.test(url);

const baseStyle = {
  textDecoration: 'none',
  color: 'inherit',
  display: 'inline-block',
};

interface Params {
  eventCategory: string;
  eventAction: string;
  eventLabel: string;
  transport?: string;
  hitCallback?: () => void;
}
const Linkable = ({
  children,
  to,
  onClick,
  target,
  className,
  style,
  isFullWidth,
}: LinkableProps) => (
  <If
    condition={isUrlExternal(to)}
    ifRender={() => (
      <Pressable
        isDefaultBehaviorAllowed={true}
        fullWidth={isFullWidth}
        onPress={() => {
          const params = {
            eventCategory: 'Outbound',
            eventAction: 'Click',
            eventLabel: to,
            ...(target === '_blank'
              ? {
                  transport: 'beacon',
                  hitCallback: () => {
                    document.location;
                  },
                }
              : {}),
          };
          // TODO: Shouldn't use ga global
          if (window.ga !== undefined) {
            window.ga('send', 'event', params);
          }
          onClick();
        }}
      >
        {({ isPressed }) => (
          <Hoverable fullWidth={isFullWidth}>
            {({ isHovering }) => (
              <a
                className={className}
                style={{
                  ...baseStyle,
                  ...style,
                  ...(isFullWidth ? { width: '100%' } : {}),
                }}
                rel={target === '_blank' ? 'noopener noreferrer' : ''}
                data-vars-outbound-link={to}
                href={to}
                target={target}
              >
                {children({ isPressed, isHovering })}
              </a>
            )}
          </Hoverable>
        )}
      </Pressable>
    )}
    elseRender={() => (
      <Pressable
        isDefaultBehaviorAllowed={true}
        fullWidth={isFullWidth}
        onPress={() => {
          onClick();
        }}
      >
        {({ isPressed }) => (
          <Hoverable fullWidth={isFullWidth}>
            {({ isHovering }) => (
              <ReactRouterDOMLink
                to={to}
                className={className}
                style={{
                  ...baseStyle,
                  ...style,
                  ...(isFullWidth ? { width: '100%' } : {}),
                }}
              >
                {children({ isPressed, isHovering })}
              </ReactRouterDOMLink>
            )}
          </Hoverable>
        )}
      </Pressable>
    )}
  />
);

const LinkText = ({
  isInline,
  color,
  isIncludingSpacesAround,
  children,
  to,
  onClick,
  target,
}: LinkProps) => {
  const linkStyles = {
    display: isInline ? 'inline' : 'block',
  };
  return (
    <Linkable
      className={cx({
        root: true,
        gray: color === 'gray',
        purple: color === 'purple',
      })}
      style={{
        cursor: 'pointer',
        ...linkStyles,
      }}
      to={to}
      onClick={onClick}
      target={target}
    >
      {({ isPressed, isHovering }) =>
        isIncludingSpacesAround ? addSpacesAround(children) : children
      }
    </Linkable>
  );
};

Linkable.defaultProps = linkableDefaultProps;
LinkText.defaultProps = linkTextDefaultProps;

export { LinkText, Linkable };
